/* eslint-disable react-hooks/exhaustive-deps */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useBaseService from "../../../hooks/services/useBaseService";
import useLoader from "../../../hooks/useLoader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useForm from "../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import { productFields } from "../../../constants/forms/almoxarifado/productFields";
import { useCallback, useEffect, useMemo, useState } from "react";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../../hooks/useSelectOptions";
import { generateProductName } from "../../../utils/generateProductName";
import Form from "../Form";
import SectionHeader from "../SectionHeader";
import SimpleBody from "../SimpleBody";
import { entities } from "../../../constants/entities";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";

export function ModalFormRegisterProduct({ open, onClose }) {
  const navigate = useNavigate();

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      "items.condition.name": [],
      "items.category.name": [],
      "items.address.street": [],
      "items.address.alias": [],
      "items.address.number": [],
      "items.address.city": [],
      "items.address.state": [],
      deleted_at: false,
    },
    "almox_products",
  );

  // 1. Inicializa useForm passando as definições dos campos (productFields)
  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(productFields),
    productFields,
  );

  // O nome do produto é gerado automaticamente
  const [productName, setProductName] = useState("");

  const typeSelect = useRemoteSelectOptions({
    queryKey: ["typeOptions"],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: "product.type_id",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.id, label: type.name }),
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const categorySelect = useRemoteSelectOptions({
    queryKey: ["categoryOptions"],
    entityPath: entities.categories.get,
    baseServiceGet,
    fieldId: "product.category_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (category) => ({ value: category.id, label: category.name }),
  });

  const groupsSelect = useRemoteSelectOptions({
    queryKey: ["groupsOptions"],
    entityPath: entities.groups.get,
    baseServiceGet,
    fieldId: "product.group_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (group) => ({ value: group.id, label: group.name }),
  });

  // Crie um array de rawOptions para o useSelectOptions
  const isPatrimonyOptionsRaw = useMemo(
    () => [
      { id: true, name: "Sim" },
      { id: false, name: "Não" },
    ],
    [],
  );

  const assetSelect = useSelectOptions({
    fieldId: "product.is_asset",
    formData,
    rawOptions: isPatrimonyOptionsRaw,
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  });

  const brandSelect = useRemoteSelectOptions({
    queryKey: ["brandOptions"],
    entityPath: entities.brands.get,
    baseServiceGet,
    fieldId: "product.brand_id",
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (brand) => ({ value: brand.id, label: brand.name }),
  });

  const selects = {
    "product.type_id": typeSelect,
    "product.category_id": categorySelect,
    "product.group_id": groupsSelect,
    "product.brand_id": brandSelect,
    "product.is_asset": assetSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  // --- Seta o uuid do produto ---
  useEffect(() => {
    // Gera um número aleatório com 8 caracteres
    const randomCode = Math.floor(10000000 + Math.random() * 90000000);
    handleChange("product.code", randomCode.toString());
  }, []);

  // Gera o nome do produto
  useEffect(() => {
    const type = typeSelect.selectedValue?.label;
    const group = groupsSelect.selectedValue?.label;
    const brand = brandSelect.selectedValue?.label;

    if (type && group && brand) {
      setProductName(generateProductName({ type, brand, group }));
    } else if (type && group) {
      setProductName(generateProductName({ type, group }));
    }
  }, [
    typeSelect.selectedValue,
    groupsSelect.selectedValue,
    brandSelect.selectedValue,
  ]);

  // setar o nome do produto no formData
  useEffect(() => {
    if (productName) {
      handleChange("product.name", productName);
    }
  }, [productName]);

  const createProduct = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(entities.products.create, data);
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader();
        resetForm();
        onClose();
        queryClient.invalidateQueries([
          "almox_products",
          transformFiltersForUrl(filters),
          pagination,
        ]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      const cleanedProduct = cleanPayload(formData);
      const payload = {
        product: {
          ...cleanedProduct.product,
          code: String(cleanedProduct.product.code),
        },
      };
      return await createProduct.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar produto:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(`/almoxarifado/produtos/`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus>
      <DialogTitle>Cadastrar Produto</DialogTitle>
      <DialogContent>
        <div className="container-fluid p-1">
          <Form
            initialFormData={formData}
            onSubmit={handleSubmit}
            textSubmit="Cadastrar Produto"
            textLoadingSubmit="Cadastrando..."
            handleBack={handleBack}
            isLoading={createProduct.isPending}
            showButtons={false}>
            {() => {
              const renderedSections = new Set();

              return productFields.map((section, index) => {
                const sectionType = section.section.split(" - ")[0];
                const showHeader = !renderedSections.has(sectionType);

                if (showHeader) {
                  renderedSections.add(sectionType);
                }

                return (
                  <div key={section.section + " " + index}>
                    {showHeader && (
                      <SectionHeader
                        section={section.section}
                        viewTable={false}
                        setViewTable={() => {}}
                        addFieldsInData={() => {}}
                      />
                    )}

                    <SimpleBody
                      fields={section.fields}
                      formErrors={normalizeFormErrors(formErrors)}
                      formData={formData}
                      handleFieldChange={handleChange}
                      getOptions={getOptions}
                      getSelectedValue={getSelectedValue}
                      getSelectMeta={getSelectMeta}
                    />
                  </div>
                );
              });
            }}
          </Form>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} color="primary">
          Cadastrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
